import { ICommonSettings, IFirebaseIOSSetting, IImageLibraryDto } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigFilePicker } from "../config-file-picker";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigFirebaseIOSSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingFirebase?: IFirebaseIOSSetting;
  allImage?: IImageLibraryDto[];
  imageSearchText: string;
  fileName: string;
}

export interface IConfigFirebaseIOSSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingFirebaseId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigFirebaseIOSSettings extends React.PureComponent<
  IConfigFirebaseIOSSettingsProps,
  IConfigFirebaseIOSSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigFirebaseIOSSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingFirebase: undefined,
      allImage: [],
      imageSearchText: "",
      fileName: ""
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigFirebaseIOSSettingsProps) {
    if (prevProps.editingFirebaseId !== this.props.editingFirebaseId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingFirebaseId) {
      const fb = this.props.commonSettings.iosFirebaseSettings?.find(
        (x) => x.id === this.props.editingFirebaseId && !x.isGlobalCommonConfigOnProjectConfig
      );

      this.setState({ editingFirebase: fb }, () => {
        if (fb) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          fieldsValue.firebaseName = fb.name;
          fieldsValue.googleServiceInfoPlistFileName = fb.googleServiceInfoPlistFileName;

          this.formRef.current?.setFieldsValue(fieldsValue);
          this.setState({ fileName: fb.googleServiceInfoPlistFileName });
        }
      });
    } else {
      this.setState({ fileName: "" });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const setting = this.props.commonSettings.iosFirebaseSettings;

    const isSettingAlreadyExist =
      setting?.some(
        (x) => x.name === value && x.id !== this.props.editingFirebaseId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Firebase Setting name is already exist.");
  };

  onFinish = (data: any) => {
    if (!this.state.fileName) {
      KMainFunctions.displayWarningNotification("Notification certificate is required.");
      return;
    }
    this.saveStatus.start();
    let obj: IFirebaseIOSSetting;
    if (this.props.editingFirebaseId) {
      obj = {
        id: this.props.editingFirebaseId,
        name: data.firebaseName,
        googleServiceInfoPlistFileName: this.state.fileName
      };

      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "iosFirebaseSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.firebaseName,
        googleServiceInfoPlistFileName: this.state.fileName
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "iosFirebaseSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteFirebase = () => {
    if (!this.props.editingFirebaseId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingFirebaseId || "",
      configKey: "iosFirebaseSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Firebase setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Firebase Setting"
        secondHeader="Firebase Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingFirebaseId ? this.deleteFirebase : undefined}
            deleteTestId="iosFirebaseSettingDeleteButton"
            submitTestId={`iosFirebaseSetting${this.props.editingFirebaseId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingFirebaseId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configFirebaseIosForm"
          />
        }
      >
        <Form
          id="configFirebaseIosForm"
          name="iosFirebaseSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("ios_firebasesettingspopup_description")}>
            <ConfigField label="Name" required tooltipContentKey={"ios_firebasesettingspopup_name"}>
              <Form.Item
                name="firebaseName"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Firebase Setting name"
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Firebase Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="iosFirebaseSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Notification Certificate"
              required
              tooltipContentKey={"ios_firebasesettingspopup_description"}
            >
              <ConfigFilePicker
                files={this.state.allImage ?? []}
                acceptedFileTypes=".plist"
                getImageLibrary={this.getImageLibrary}
                selectedId={
                  this.state.allImage?.find((x: IImageLibraryDto): boolean => x.fileName === this.state.fileName)?.id
                }
                fileName={this.state.fileName}
                onSelect={(image: IImageLibraryDto): void => this.setState({ fileName: image.fileName })}
                onClear={(): void => this.setState({ fileName: "" })}
                chooseTestId={`iosFirebaseSetting${this.state.fileName ? "REPLACE" : "CHOOSE"}Button`}
              />
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
